// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Grid2, Stack, Switch, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseStatus } from '~/components/common';
// Others
import { IBodyUpdate } from '~/utils/interface/common';
import { orderActions } from '~/thunks/order/orderSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { changeStatusOrder } from '~/thunks/order/orderThunk';
import { RingCentralContext } from '~/context';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { IBodyChangeStatusOrder, IOrderDetail, IOrderDetailInvoice } from '~/utils/interface/order';
import { BusinessTypeEnum, ConnectionStatusEnum, DateFormatEnum, OrderStatusEnum } from '~/utils/enum/common';
import { formatCurrencyUSD, formatDate, formatPhoneNumber, formatTitleCase, getFullName } from '~/utils/helper/common';
// Styles, images, icons
import styles from './InformationOrder.module.scss';
import { icons } from '~/assets';

type Props = {
  detailOrder?: IOrderDetail | IOrderDetailInvoice;
};

const cx = classNames.bind(styles);

const InformationOrder = (props: Props) => {
  //#region Destructuring Props
  const { detailOrder } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const { ringCentralStatus, executeCall } = useContext(RingCentralContext);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [status, setStatus] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (detailOrder) {
      setStatus(detailOrder.status === OrderStatusEnum.FINALIZED);
    }
  }, [detailOrder]);
  //#endregion Implement Hook

  //#region Handle Function
  const renderItem = (title: string, children: React.ReactNode) => {
    return (
      <Stack className={cx('item')}>
        <Typography className={cx('label')}>{title}</Typography>

        <Box className={cx('value')}>{children}</Box>
      </Stack>
    );
  };

  const handleClickPhoneNumber = () => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) {
      alert(t('Ringcentral is not connected'));
      return;
    }

    if (detailOrder?.client?.clientContacts?.[0]?.phoneNumber && detailOrder.client?.clientId) {
      executeCall &&
        executeCall(detailOrder?.client?.clientContacts?.[0]?.phoneNumber, detailOrder?.id, BusinessTypeEnum.ORDER);
    }
  };

  const handleChangeStatusOrder = (status: string) => {
    if (!detailOrder?.id) return;

    const paramChangeStatus: IBodyUpdate<IBodyChangeStatusOrder> = {
      id: detailOrder?.id,
      body: {
        status: status,
      },
    };

    dispatch(changeStatusOrder(paramChangeStatus))
      .unwrap()
      .then((_res) => {
        dispatch(orderActions.setRefreshOrderDetail(true));
      })
      .catch((_error) => {})
      .finally(() => {});
  };
  //#endregion Handle Function

  return (
    <Grid2 container spacing={4} className={cx('container')}>
      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconPlusPrimary}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('order_detail_title_transaction')}</Typography>
          </Stack>

          {renderItem(
            t('order_detail_title_title'),
            <Typography className={cx('value')}>{detailOrder?.eventTitle || EMPTY_VALUE}</Typography>
          )}

          {renderItem(
            t('order_detail_title_order_id'),
            <Typography className={cx('value')}>
              {detailOrder?.orderNumber ? `#${detailOrder?.orderNumber}` : EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_invoice_id'),
            <Typography className={cx('value')}>
              {detailOrder?.invoice?.map((item) => item?.id).join(', ') || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_status'),
            <>{detailOrder?.status ? <BaseStatus label={detailOrder?.status} /> : EMPTY_VALUE}</>
          )}

          {renderItem(
            t('order_detail_title_finalized'),
            <>
              <Switch
                checked={status}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const newStatus = checked ? OrderStatusEnum.FINALIZED : OrderStatusEnum.BOOKED;

                  setStatus(checked);
                  handleChangeStatusOrder(newStatus);
                }}
                slotProps={{ input: { 'aria-label': 'controlled' } }}
              />
            </>
          )}
        </Stack>
      </Grid2>

      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconPayment}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('order_detail_title_payment')}</Typography>
          </Stack>

          {renderItem(
            t('order_detail_title_order_total'),
            <Typography className={cx('value')}>
              {formatCurrencyUSD({ value: detailOrder?.total, showDecimal: true }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_amount_paid'),
            <Typography className={cx('value')}>
              {formatCurrencyUSD({
                value: detailOrder?.amountPaid,
                showDecimal: true,
              }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_amount_due'),
            <Typography className={cx('textPrice')}>
              {formatCurrencyUSD({ value: detailOrder?.amountDue, showDecimal: true }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_payment_status'),
            <BaseStatus label={detailOrder?.paymentStatus || EMPTY_STRING} />
          )}

          {renderItem(
            t('order_detail_title_payment_terms'),
            <Typography className={cx('paymentTerms')}>
              {detailOrder?.paymentTerms ? formatTitleCase(detailOrder?.paymentTerms) : EMPTY_VALUE}
            </Typography>
          )}
        </Stack>
      </Grid2>

      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconClockTime}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('order_detail_title_order_date')}</Typography>
          </Stack>

          {renderItem(
            t('order_detail_title_start_date'),
            <Typography className={cx('value')}>
              {formatDate(detailOrder?.startDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_end_date'),
            <Typography className={cx('value')}>
              {formatDate(detailOrder?.endDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE}
            </Typography>
          )}

          {/* {renderItem(t('order_detail_title_duration'), <Typography className={cx('value')}>{EMPTY_VALUE}</Typography>)} */}

          {renderItem(
            t('order_detail_title_event_date'),
            <Typography className={cx('value')}>
              {formatDate(detailOrder?.startDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_managed_by'),
            <Typography className={cx('value')}>
              {getFullName({
                firstName: detailOrder?.managedBy?.firstName,
                lastName: detailOrder?.managedBy?.lastName,
              }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_salesperson'),
            <Typography className={cx('value')}>
              {getFullName({
                firstName: detailOrder?.salesPerson?.firstName,
                lastName: detailOrder?.salesPerson?.lastName,
              }) || EMPTY_VALUE}
            </Typography>
          )}
        </Stack>
      </Grid2>

      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconPerson}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('order_detail_title_client_info')}</Typography>
          </Stack>

          {renderItem(
            t('order_detail_title_name'),
            <Typography className={cx('value')}>
              {getFullName({
                firstName: detailOrder?.client?.firstName,
                lastName: detailOrder?.client?.lastName,
              }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_company'),
            <Typography className={cx('value')}>{detailOrder?.client?.companyName || EMPTY_VALUE}</Typography>
          )}

          {renderItem(
            t('order_detail_title_address'),
            <Typography className={cx('value')}>{detailOrder?.clientAddress?.address || EMPTY_VALUE}</Typography>
          )}

          {renderItem(
            t('order_detail_title_email'),
            <Typography className={cx('value')}>
              {detailOrder?.client?.clientContacts?.[0]?.email || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('order_detail_title_phone'),
            <Typography className={cx('value', 'phoneNumber')} onClick={handleClickPhoneNumber}>
              {formatPhoneNumber(detailOrder?.client?.clientContacts?.[0]?.phoneNumber || EMPTY_STRING) || EMPTY_VALUE}
            </Typography>
          )}
        </Stack>
      </Grid2>
    </Grid2>
  );
};

export default InformationOrder;
